import { get } from 'lodash-es'
import TmLogicError from '@/core/error/tmLogicError'

const getPath = Symbol('getPathKey')

export const ARRAY_ITEM_PATH_WILDCARD = '*'

function createProxyObject<T>(parentPath: string[]) {
  const proxy: T = new Proxy<any>(
    {},
    {
      get(target: Record<string, unknown>, key: string | symbol, receiver: any): any {
        if (key === getPath) {
          return parentPath
        }
        const pathToField = [...parentPath]
        if (parentPath !== null) {
          pathToField.push(key as string)
        }
        if (!Reflect.has(target, key)) {
          Reflect.set(target, key, createProxyObject(pathToField))
        }
        return Reflect.get(target, key, receiver)
      },
    },
  )
  return proxy
}

export function getTreePathForObjectField<T>(callback: (t: T) => any) {
  const proxyObject = createProxyObject<T>([])
  const callbackRes = callback(proxyObject)
  return callbackRes[getPath] as string[]
}

export function getTreePathsForObjectFields<T>(callback: (t: T) => any[]) {
  const proxyObject = createProxyObject<T>([])
  const callbackRes = callback(proxyObject)
  return callbackRes.map((item) => item[getPath] as string[])
}

export function eachItem<T>(arrayField: T[]): T {
  const path = (arrayField as { [getPath]?: string[] })[getPath]
  if (!path) {
    throw new TmLogicError('eachItem() can only be used inside typed field getters')
  }
  return createProxyObject<T>([...path, ARRAY_ITEM_PATH_WILDCARD])
}

export function expandArrayItemFieldPaths(paths: string[][], sources: unknown[]): string[][] {
  return paths.flatMap((path) => expandArrayItemFieldPath(path, sources))
}

function expandArrayItemFieldPath(path: string[], sources: unknown[]): string[][] {
  const wildcardIndex = path.indexOf(ARRAY_ITEM_PATH_WILDCARD)
  if (wildcardIndex === -1) {
    return [path]
  }

  const arrayPath = path.slice(0, wildcardIndex)
  const restPath = path.slice(wildcardIndex + 1)
  const length = Math.max(
    0,
    ...sources.map((source) => {
      const value = arrayPath.length ? get(source, arrayPath) : source
      return Array.isArray(value) ? value.length : 0
    }),
  )

  return Array.from({ length }, (_, index) =>
    expandArrayItemFieldPath([...arrayPath, String(index), ...restPath], sources),
  ).flat()
}
